/**
 * Normalización de estados de llamada Telnyx WebRTC (@telnyx/webrtc).
 * El SDK usa el enum numérico `State` (0–11), no solo strings.
 * @see node_modules/@telnyx/webrtc/.../constants.d.ts — enum State
 */

export type TelnyxCallDirection = 'inbound' | 'outbound';

/**
 * Convierte `call.state` (string o número del enum State) a nombre estable.
 */
function stateFromEnumNumber(n: number): string {
  switch (n) {
    case 0:
      return 'new';
    case 1:
      return 'requesting';
    case 2:
      return 'trying';
    case 3:
      return 'recovering';
    case 4:
      return 'ringing';
    case 5:
      return 'answering';
    case 6:
      return 'early';
    case 7:
      return 'active';
    case 8:
      return 'held';
    case 9:
      return 'hangup';
    case 10:
      return 'destroy';
    case 11:
      return 'purge';
    default:
      return String(n);
  }
}

export function normalizeTelnyxCallState(state: string | number | undefined): string {
  if (state === undefined || state === null) return '';
  if (typeof state === 'number') {
    return stateFromEnumNumber(state);
  }
  if (typeof state === 'string') {
    const t = state.trim();
    if (t === '') return '';
    /** El SDK a veces manda el enum como string ("4" = ringing); antes quedaba "4" y no coincidía con ringback. */
    if (/^\d+$/.test(t)) {
      const n = Number(t);
      if (Number.isFinite(n)) return stateFromEnumNumber(n);
    }
    return t.toLowerCase();
  }
  return String(state).toLowerCase();
}

export function isTerminalTelnyxState(stateNorm: string): boolean {
  return stateNorm === 'hangup' || stateNorm === 'destroy' || stateNorm === 'purge' || stateNorm === 'ended' || stateNorm === 'bye' || stateNorm === 'done';
}

/** Solo llamadas entrantes en ringing deben contestarse automáticamente (doc Telnyx). */
export function shouldAutoAnswerInbound(stateNorm: string, direction: TelnyxCallDirection | ''): boolean {
  return direction === 'inbound' && stateNorm === 'ringing';
}

/**
 * Timbrado local (ringback) en salientes mientras el destino no ha contestado.
 * Debe incluir `ringing`: es cuando el PSTN está timbrando; si no, el tono local se cortaba
 * justo al pasar de trying → ringing y parecía que “nunca sonaba”.
 * `early` = 183 Session Progress (a veces sin audio al navegador); seguimos timbrando por si no hay RTP.
 */
export function shouldPlayOutboundLocalRingback(
  stateNorm: string,
  direction: TelnyxCallDirection | ''
): boolean {
  if (direction !== 'outbound') return false;
  const s = stateNorm.trim();
  /** Sin estado aún: la UI sigue en “timbrando”; el timbrado local debe seguir. */
  if (!s) return true;
  return (
    s === 'new' ||
    s === 'trying' ||
    s === 'requesting' ||
    s === 'recovering' ||
    s === 'ringing' ||
    s === 'early' ||
    s === 'answering' ||
    s === 'outbound_ui_pending'
  );
}

export type WebrtcCallUiPhase = 'none' | 'ringing' | 'active';

/**
 * Fase UI solo desde el SDK (sin CRM). Entradas/salidas que no son Llamadas PSTN bridge.
 */
export function deriveCallUiPhase(stateNorm: string): WebrtcCallUiPhase {
  const s = stateNorm.trim();
  if (!s) return 'ringing';
  if (isTerminalTelnyxState(s)) return 'none';
  if (s === 'active' || s === 'held') return 'active';
  return 'ringing';
}

/**
 * Fase UI para llamadas con fila `crm_calls` (marcación saliente con bridge Telnyx).
 *
 * **Causa raíz del timbrado en silencio + cronómetro corriendo:** el SDK pasa a `active` en cuanto
 * la pata WebRTC del agente tiene audio hacia Telnyx; eso NO significa que el destino PSTN haya
 * contestado. El webhook deja `crm_calls.call_status` en `ringing` hasta `answered` (2.ª pata).
 * Si la UI usa solo `deriveCallUiPhase` → `active` → cronómetro + `playLocalRingback` false.
 *
 * Regla: en **saliente**, `active` (conversación + cronómetro) solo si CRM `answered`.
 * Mientras tanto la fase UI sigue siendo **`ringing`** (timbrando) y el timbrado local puede sonar.
 *
 * **`direction` debe ser la del negocio (CRM), no la `call.direction` del SDK:** en bridge WebRTC
 * la pata al navegador suele ser **inbound** en el SDK aunque la llamada sea **saliente** al lead.
 */
export function deriveCallUiPhaseWithCrm(
  stateNorm: string,
  crmCallStatus: string | null,
  direction: TelnyxCallDirection | null
): WebrtcCallUiPhase {
  const s = stateNorm.trim();
  if (!s) return 'ringing';
  if (isTerminalTelnyxState(s)) return 'none';

  const outbound = direction === 'outbound';
  if (outbound && (s === 'active' || s === 'held')) {
    const crm = crmCallStatus?.trim().toLowerCase() ?? '';
    if (crm === 'answered') return 'active';
    return 'ringing';
  }

  if (s === 'active' || s === 'held') return 'active';
  return 'ringing';
}

/** Timbrado local en saliente: solo mientras la fase UI es “timbrando”, no “en conversación”. */
export function shouldPlayLocalRingbackFromPhase(
  phase: WebrtcCallUiPhase,
  direction: TelnyxCallDirection | null
): boolean {
  return direction === 'outbound' && phase === 'ringing';
}

/**
 * Misma intención que `uiRinging` en `CallCenterPhoneActiveSession`: el SDK puede estar `active` y aún
 * mostrar “Timbrando” (sin `crm_calls.id` o sin `answered`). Antes `playLocalRingback` solo miraba
 * `phase === 'ringing'`, así que el bucle de timbrado se apagaba mientras la UI seguía en timbrado.
 *
 * Además, si `businessDir` llega como `inbound` en un bridge saliente pero hay fila CRM o
 * `outbound_ui_pending`, seguimos generando tono como en la llamada manual.
 */
export function shouldPlaySyntheticRingbackForCallCenter(
  telnyxStateNorm: string,
  crmCallStatus: string | null,
  crmCallId: string | null,
  callPhase: WebrtcCallUiPhase,
  businessDir: TelnyxCallDirection | null,
): boolean {
  const s = telnyxStateNorm.trim();
  if (isTerminalTelnyxState(s)) return false;
  if (callPhase === 'none') return false;

  const crm = crmCallStatus?.trim().toLowerCase() ?? '';
  const pstnAnswered = !!(crmCallId?.trim() && crm === 'answered');
  const uiRinging = callPhase === 'ringing' || (callPhase === 'active' && !pstnAnswered);
  if (!uiRinging) return false;

  const hasCrmRow = !!crmCallId?.trim();
  const outboundish =
    businessDir === 'outbound' ||
    hasCrmRow ||
    s === 'outbound_ui_pending';

  if (!outboundish) return false;

  if (shouldPlayOutboundLocalRingback(s, 'outbound')) return true;

  /** WebRTC ya `active`/`held` pero PSTN aún no contestó (CRM sin `answered` o sin fila aún). */
  if ((callPhase === 'active' || callPhase === 'ringing') && !pstnAnswered) return true;

  return false;
}
